<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import {
  MolModal,
  SatElasticPanel,
  OrgMessageModal,
  OrgLoadingModal,
  AtomButton,
} from "@chatfood/design-system";
import { AuthManager } from "../auth-manager";
import { CheckoutView } from "../checkout-view";
import { AddressForm } from "../address-form";
import { DiscountsList } from "../discounts-list";
import { OrderForLaterWarn } from "../order-for-later-warn";
import { useModalHubStore } from "./use-modal-hub";

const css = bemBuilder("modal-hub");
const store = useModalHubStore();
</script>

<template>
  <div :class="css('')">
    <SatElasticPanel
      v-model="store.isCheckoutOn"
      show-close
      width-fit-content
      full-height
      lock-body
      with-transparent-fade
      padding="none"
      h-position="center"
      @update:model-value="store.hideCheckout"
    >
      <CheckoutView @close="store.hideCheckout" />
    </SatElasticPanel>
    <MolModal
      :show-close="true"
      width-fit-content
      :model-value="store.isAddressFormOn"
      @update:model-value="store.hideAddressForm"
      @close="store.hideAddressForm"
    >
      <AddressForm
        @created="store.hideAddressForm"
        @refined="store.refineAddressForm"
      />
    </MolModal>
    <SatElasticPanel
      v-model="store.isAuthManagerOn"
      show-close
      width-fit-content
      with-transparent-fade
      padding="large"
      h-position="center"
      @update:model-value="store.hideAuthManager"
    >
      <AuthManager
        :allow-proceed-as-guest="store.authAllowProceedAsGuest"
        @complete="store.hideAuthManager"
      />
    </SatElasticPanel>
    <SatElasticPanel
      v-model="store.isDiscountsOn"
      show-close
      width-fit-content
      padding="none"
    >
      <DiscountsList @dismiss="store.hideDiscounts" />
    </SatElasticPanel>
    <OrgLoadingModal
      v-model="store.isLoadingOn"
      :primary-text="store.loadingTitle"
      :secondary-text="store.loadingMessage"
    />
    <OrgMessageModal
      v-model="store.isErrorOn"
      :primary-text="store.errorTitle"
      :secondary-text="store.errorMessage"
    >
      <AtomButton
        v-if="store.errorCtaLabel"
        wide
        size="large"
        @click="store.hideError"
      >
        {{ store.errorCtaLabel }}
      </AtomButton>
    </OrgMessageModal>
    <OrgMessageModal
      v-model="store.isWarningOn"
      :primary-text="store.warningTitle"
      :secondary-text="store.warningMessage"
    >
      <div :class="css('cta-wrapper')">
        <AtomButton
          v-if="store.onWarningConfirm"
          size="large"
          :wide="true"
          @click="store.onWarningConfirm"
        >
          {{ store.warningConfirmLabel }}
        </AtomButton>
        <AtomButton
          v-if="store.onWarningDismiss"
          variant="secondary"
          size="large"
          :wide="true"
          @click="store.hideWarning"
        >
          {{ store.warningDismissLabel }}
        </AtomButton>
      </div>
    </OrgMessageModal>
    <MolModal
      v-model="store.isOrderForLaterWarnOn"
      :show-close="false"
      width-fit-content
      padding="large"
    >
      <OrderForLaterWarn @dismiss="store.hideOrderForLaterWarn" />
    </MolModal>
  </div>
</template>

<style lang="scss" scoped>
.modal-hub {
  &__cta-wrapper {
    gap: var(--spacing-sm);
    flex-direction: column;
    display: flex;
  }
}
</style>
